/**
 * PersonaHeader — top bar shown above the chat during an active simulation.
 *
 * Three regions:
 *   - Left: persona identity (name, age, profession).
 *   - Center: procedure name + price + duration.
 *   - Right: eneatype + difficulty badges, plus "Encerrar" button.
 */
import { ArrowLeft, SignOut } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TYPE_ACCENT, TYPE_CONTENT } from "@/features/decodificar/content";
import { headerMotion } from "../lib/chatMotion";
import type { EnneagramTypeId } from "@/types/supabase";
import type {
  ProcedurePublic,
  SimulatorDifficulty,
} from "../types";
import type { PersonaSnapshot } from "../content/pools";

export interface PersonaHeaderProps {
  persona: PersonaSnapshot;
  procedure: ProcedurePublic;
  difficulty: SimulatorDifficulty;
  onBack: () => void;
  onEnd: () => void;
  isEnding?: boolean;
}

function formatBRL(cents: number | null): string {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function PersonaHeader({
  persona,
  procedure,
  difficulty,
  onBack,
  onEnd,
  isEnding,
}: PersonaHeaderProps) {
  const tid = persona.eneatype as EnneagramTypeId;
  const accent = TYPE_ACCENT[tid];
  const typeName = TYPE_CONTENT[tid]?.name ?? `Tipo ${persona.eneatype}`;

  return (
    <motion.header
      className="border-b border-border/50 bg-card/60 px-4 py-3 backdrop-blur flex flex-wrap items-center gap-3"
      initial={headerMotion.initial}
      animate={headerMotion.animate}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="Voltar para simuladores"
          data-testid="back-to-simulators"
        >
          <ArrowLeft weight="bold" className="h-4 w-4" />
        </Button>
        <p className="font-medium text-foreground truncate">
          {persona.name}{" "}
          <span className="text-muted-foreground font-normal">
            · {persona.age} anos · {persona.profession}
          </span>
        </p>
      </div>

      <div className="hidden md:block text-sm text-muted-foreground truncate">
        <span className="text-foreground font-medium">{procedure.name}</span>{" "}
        · {formatBRL(procedure.value_cents)}{" "}
        {procedure.duration_minutes ? `· ${procedure.duration_minutes} min` : ""}
      </div>

      <div className="flex items-center gap-2">
        <Badge
          className="text-[11px]"
          style={{ backgroundColor: `${accent}25`, color: accent, border: "none" }}
        >
          Tipo {persona.eneatype} · {typeName}
        </Badge>
        <Badge
          variant={difficulty === "hard" ? "destructive" : "secondary"}
          className="text-[11px]"
        >
          {difficulty === "hard" ? "Difícil" : "Padrão"}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={onEnd}
          disabled={isEnding}
          className="gap-1.5 text-muted-foreground hover:text-foreground"
          data-testid="end-simulation"
        >
          <SignOut weight="bold" className="w-4 h-4" />
          <span className="hidden sm:inline">Encerrar</span>
        </Button>
      </div>
    </motion.header>
  );
}
